import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';
import { useCal } from '@/hooks/useCal';

export const LandingNavbar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { openCalModal } = useCal();

  const getActiveModule = () => {
    if (location.pathname === '/competitor') return 'Competitor';
    if (location.pathname === '/availability') return 'Availability';
    if (location.pathname === '/visibility') return 'Visibility';
    if (location.pathname === '/pricing') return 'Pricing';
    if (location.pathname === '/performance') return 'Performance';
    return null;
  };

  const activeModule = getActiveModule();

  const navItems = [
    { name: 'Competitor', path: '/competitor' },
    { name: 'Availability', path: '/availability' },
    { name: 'Visibility', path: '/visibility' },
    { name: 'Performance', path: '/performance' },
    { name: 'Pricing', path: '/pricing' }
  ];

  return (
    <div className="fixed top-4 sm:top-6 left-0 right-0 z-50 flex justify-center px-3 sm:px-4 pointer-events-none">
      <nav className="glass-pill px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 rounded-full flex items-center justify-between max-w-5xl w-full pointer-events-auto">
        {/* Logo */}
        <div className="flex items-center gap-1.5 sm:gap-2 cursor-pointer flex-shrink-0 min-w-[120px] sm:min-w-[140px] lg:min-w-[160px]" onClick={() => navigate('/')}>
          <img 
            src="/ReflowAI Logo.png" 
            alt="ReflowAI" 
            className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg object-contain"
            width="50"
            height="50"
          />
          <span className="text-base sm:text-lg md:text-xl font-bold tracking-tight text-slate-900">ReflowAI</span>
        </div>

        {/* Desktop Menu - Centered */}
        <div className="hidden lg:flex items-center justify-center gap-6 xl:gap-8 flex-1">
          {navItems.map((item) => (
            <a 
              key={item.name} 
              href={item.path}
              onClick={(e) => {
                e.preventDefault();
                navigate(item.path);
              }}
              className={`text-sm font-semibold transition-colors whitespace-nowrap ${
                item.name === activeModule 
                  ? 'text-[#2F7EDA] bg-[#2F7EDA]/10 px-3 py-1 rounded-full' 
                  : 'text-[#555663] hover:text-[#2F7EDA]'
              }`}
            >
              {item.name}
            </a>
          ))}
        </div>

        {/* Login & Get Started Buttons (Desktop) */}
        <div className="hidden lg:flex items-center justify-end gap-3 xl:gap-4 flex-shrink-0 min-w-[120px] sm:min-w-[140px] lg:min-w-[200px]">
          <button 
            className="text-sm font-bold text-[#555663] hover:text-[#2F7EDA] transition-colors whitespace-nowrap"
            onClick={() => navigate('/login')}
          >
            Log in
          </button>
          <button 
            className="bg-[#1A1B25] text-white px-4 xl:px-5 py-2 xl:py-2.5 rounded-full text-sm font-bold hover:bg-black transition-colors flex items-center gap-2 whitespace-nowrap"
            onClick={openCalModal}
          >
            Get Started <ArrowRight className="w-3 h-3" />
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          className="lg:hidden p-2 text-[#0F172A] hover:text-[#2F7EDA] transition-colors flex-shrink-0" 
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          aria-label="Toggle menu"
        >
          {isMobileOpen ? <X className="w-5 h-5 sm:w-6 sm:h-6" /> : <Menu className="w-5 h-5 sm:w-6 sm:h-6" />}
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/30 backdrop-blur-sm pointer-events-auto lg:hidden z-40"
            onClick={() => setIsMobileOpen(false)}
          />
          
          {/* Menu Content */}
          <div className="fixed top-[70px] sm:top-[82px] left-3 right-3 sm:left-4 sm:right-4 bg-white rounded-[24px] shadow-2xl pointer-events-auto lg:hidden z-50 border border-gray-200 animate-in slide-in-from-top-5 fade-in duration-200">
            <div className="flex flex-col p-6 max-h-[calc(100vh-100px)] overflow-y-auto">
              {/* Navigation Links */}
              <div className="space-y-1 mb-6">
                {navItems.map((item) => (
                  <a 
                    key={item.name} 
                    href={item.path}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(item.path);
                      setIsMobileOpen(false);
                    }}
                    className={`block px-4 py-3 rounded-xl text-base font-semibold transition-all ${
                      item.name === activeModule 
                        ? 'text-[#2F7EDA] bg-[#2F7EDA]/10' 
                        : 'text-[#1A1B25] hover:bg-gray-50 hover:text-[#2F7EDA]'
                    }`}
                  >
                    {item.name}
                  </a>
                ))}
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-200 my-4" />

              {/* Auth Buttons */}
              <div className="space-y-3">
                <button 
                  className="w-full text-center py-3 px-4 text-base font-bold text-[#1A1B25] hover:text-[#2F7EDA] transition-colors rounded-xl hover:bg-gray-50"
                  onClick={() => {
                    navigate('/login');
                    setIsMobileOpen(false);
                  }}
                >
                  Log in
                </button>
                <button 
                  className="w-full bg-[#1A1B25] text-white py-3 px-4 rounded-xl text-base font-bold hover:bg-black transition-colors shadow-lg"
                  onClick={() => {
                    openCalModal();
                    setIsMobileOpen(false);
                  }}
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Global Styles for Glass Effect */}
      <style>{`
        .glass-pill {
          background: rgba(252, 253, 253, 0.6); 
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.8);
          box-shadow: 0 10px 40px -10px rgba(47, 126, 218, 0.1);
        }
      `}</style>
    </div>
  );
};
